import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  TemplateRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { form, FormField, submit, required, min } from '@angular/forms/signals';
import { HlmAlertDialogImports } from '@spartan-ng/helm/alert-dialog';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmComboboxImports } from '@spartan-ng/helm/combobox';
import { HlmInput } from '@spartan-ng/helm/input';
import { HlmLabel } from '@spartan-ng/helm/label';
import { HlmSpinner } from '@spartan-ng/helm/spinner';
import { toast } from '@spartan-ng/brain/sonner';
import { LucideTrendingUp, LucideRefreshCw, LucideCalendar } from '@lucide/angular';
import { RatesService, Rate } from '../../core/services/rates.service';
import { CatalogService } from '../../core/services/catalog.service';
import { DataTableComponent } from '../../shared/data-table/data-table.component';
import { DataTableCellContext, DataTableColumn } from '../../shared/data-table/data-table.types';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-rates',
  standalone: true,
  imports: [
    CommonModule,
    FormField,
    HlmAlertDialogImports,
    HlmButton,
    HlmComboboxImports,
    HlmInput,
    HlmLabel,
    HlmSpinner,
    DataTableComponent,
    LucideTrendingUp,
    LucideRefreshCw,
    LucideCalendar
  ],
  templateUrl: './rates.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RatesComponent implements OnInit {
  private readonly ratesService = inject(RatesService);
  private readonly catalogService = inject(CatalogService);

  // Historial completo de tasas expuesto desde el servicio
  protected readonly rates = this.ratesService.rates;

  // Catálogo de monedas para el selector
  protected readonly currencies = this.catalogService.currencies;

  // Moneda seleccionada en el filtro (por defecto USD)
  protected readonly selectedCurrency = signal<string>('USD');

  /**
   * Etiqueta mostrada/buscada en el combobox de monedas. Convierte el código
   * seleccionado en "CÓDIGO - Nombre" para visualización y filtrado.
   */
  protected readonly currencyLabel = (code: string): string => {
    const currency = this.currencies().find((c) => c.code === code);
    return currency ? `${currency.code} - ${currency.name}` : (code ?? '');
  };

  /** Etiqueta del combobox de fuente: identidad simple del valor. */
  protected readonly sourceLabel = (value: string): string => value ?? '';

  // Historial filtrado según la moneda seleccionada
  protected readonly filteredRates = computed(() =>
    this.rates().filter((rate) => rate.currency_code === this.selectedCurrency())
  );

  // Estado local para rastrear qué tasa se está editando
  protected readonly selectedRateId = signal<number | null>(null);

  // Plantilla para la celda de "Estado" (badge), definida en el HTML
  private readonly statusCell = viewChild<TemplateRef<DataTableCellContext<Rate>>>('statusCell');

  // Devuelve el identificador de una fila para resaltar la seleccionada
  protected readonly rateRowId = (rate: Rate): number => rate.rate_id;

  // Definición dinámica de columnas para el data-table del historial
  protected readonly historyColumns = computed<DataTableColumn<Rate>[]>(() => [
    {
      key: 'value_date', header: 'Fecha Valor', accessor: (r) => r.value_date, sortable: true,
      cellClass: 'font-medium text-slate-800'
    },
    {
      key: 'rate_from', header: 'Compra', accessor: (r) => r.rate_from, align: 'right', sortable: true,
      cellClass: 'font-mono text-xs'
    },
    {
      key: 'rate_to', header: 'Venta', accessor: (r) => r.rate_to, align: 'right', sortable: true,
      cellClass: 'font-mono text-xs'
    },
    {
      key: 'rate_average', header: 'Promedio', accessor: (r) => r.rate_average, align: 'right', sortable: true,
      cellClass: 'font-mono text-xs font-semibold text-slate-800'
    },
    { key: 'source', header: 'Fuente', accessor: (r) => r.source, sortable: true, cellClass: 'text-slate-700' },
    { key: 'status', header: 'Estado', align: 'center', accessor: (r) => r.status, cell: this.statusCell() },
  ]);

  // Indicadores de carga para mostrar spinners durante las peticiones
  protected readonly isLoadingHistory = signal(false);
  protected readonly isSubmitting = signal(false);

  // Controla la visibilidad del diálogo de confirmación de aprobación de tasa
  protected readonly isApproveDialogOpen = signal(false);

  // Modelo del formulario compatible con Signal Forms
  protected readonly ratesFormModel = signal({
    rate_average: 0,
    rate_from: 0,
    rate_to: 0,
    rate_id: 0,
    source: 'BACKOFFICE',
  });

  // Estructura y reglas de validación
  protected readonly ratesForm = form(this.ratesFormModel, (s) => {
    required(s.rate_average, { message: 'El promedio es obligatorio' });
    min(s.rate_average, 0.01, { message: 'El promedio debe ser mayor a 0' });

    required(s.rate_from, { message: 'La tasa de compra es obligatoria' });
    min(s.rate_from, 0.01, { message: 'La tasa de compra debe ser mayor a 0' });

    required(s.rate_to, { message: 'La tasa de venta es obligatoria' });
    min(s.rate_to, 0.01, { message: 'La tasa de venta debe ser mayor a 0' });

    required(s.source, { message: 'La fuente es obligatoria' });
  });

  ngOnInit(): void {
    this.loadCurrencies();
    this.loadRatesHistory();
  }

  /**
   * Carga el catálogo de monedas para llenar el selector de filtro.
   */
  loadCurrencies(): void {
    this.catalogService.getCurrencies().subscribe({
      error: (err) => {
        toast.error('No se pudo cargar el listado de monedas.');
      }
    });
  }

  /**
   * Carga el historial de tasas. No selecciona ninguna tasa por defecto: el usuario
   * debe elegir una fila para comenzar a editar.
   */
  loadRatesHistory(): void {
    this.isLoadingHistory.set(true);
    this.ratesService.getRatesHistory().subscribe({
      next: () => {
        this.clearSelection();
      },
      error: (err) => {
        console.error('Error al cargar historial de tasas:', err);
        toast.error('No se pudo cargar el historial de tasas.');
      }
    }).add(() => this.isLoadingHistory.set(false));
  }

  /**
   * Cambia la moneda del filtro y limpia la selección actual.
   */
  onCurrencyChange(code: string | null | undefined): void {
    if (!code) {
      return;
    }
    this.selectedCurrency.set(code);
    this.clearSelection();
  }

  /**
   * Limpia la tasa seleccionada para volver al estado "Selecciona una Tasa".
   */
  private clearSelection(): void {
    this.selectedRateId.set(null);
  }

  /**
   * Carga los datos de una tasa seleccionada en el formulario de edición.
   */
  selectRate(rate: Rate): void {
    this.selectedRateId.set(rate.rate_id);
    this.ratesFormModel.set({
      // Se redondea a 4 decimales: la edición manual sólo admite esa precisión.
      rate_average: this.roundTo4(parseFloat(rate.rate_average)),
      rate_from: this.roundTo4(parseFloat(rate.rate_from)),
      rate_to: this.roundTo4(parseFloat(rate.rate_to)),
      rate_id: rate.rate_id,
      // La fuente para tasas modificadas desde el backoffice es siempre BACKOFFICE.
      source: 'BACKOFFICE',
    });
  }

  /**
   * Redondea un número a un máximo de 4 decimales, devolviendo 0 si no es válido.
   */
  private roundTo4(value: number): number {
    if (!Number.isFinite(value)) {
      return 0;
    }
    return Math.round(value * 10000) / 10000;
  }

  /**
   * Confirma la aprobación desde el diálogo: cierra el diálogo y envía la tasa.
   */
  confirmApproval(): void {
    this.isApproveDialogOpen.set(false);
    this.onSubmit();
  }

  /**
   * Envía los datos actualizados de la tasa al servicio.
   */
  onSubmit(): void {
    submit(this.ratesForm, async () => {
      this.isSubmitting.set(true);
      try {
        const payload = this.ratesFormModel();
        await lastValueFrom(this.ratesService.approveRate(payload));
        toast.success('Tasa de cambio modificada y aprobada con éxito');

        // Recargar el historial para ver los cambios reflejados
        this.loadRatesHistory();
      } catch (err: any) {
        console.error('Error al aprobar tasa:', err);
        const errMsg = err.message || 'Error al guardar la tasa de cambio.';
        toast.error(errMsg);
      } finally {
        this.isSubmitting.set(false);
      }
    });
  }
}
